class DisneyFigurineLookup extends HTMLElement {
  connectedCallback() {
    this.body = this.querySelector('[data-lookup-body]');
    this.searchInput = this.querySelector('[data-lookup-search]');
    this.rangeFilter = this.querySelector('[data-lookup-range]');
    this.sourceFilter = this.querySelector('[data-lookup-source]');
    this.resultCount = this.querySelector('[data-lookup-count]');
    this.resetButton = this.querySelector('[data-lookup-reset]');
    this.emptyState = this.querySelector('[data-lookup-empty]');
    this.cards = this.querySelector('[data-lookup-cards]');
    this.data = window.disneyFigurineLookupData || [];
    this.labels = {
      entry: this.dataset.labelEntry || 'entry',
      entries: this.dataset.labelEntries || 'entries',
      needsDetail: this.dataset.labelNeedsDetail || 'Needs detail',
      unknown: this.dataset.labelUnknown || 'Unknown',
      moreDetails: this.dataset.labelMoreDetails || 'More details',
      characterFilmVerified:
        this.dataset.labelCharacterFilmVerified || 'Character and film details being verified.',
      characters: this.dataset.labelCharacters || 'Character(s)',
      film: this.dataset.labelFilm || 'Film / franchise',
      series: this.dataset.labelSeries || 'Series / sub-range',
      introduced: this.dataset.labelIntroduced || 'Introduced',
      source: this.dataset.labelSource || 'Source',
    };

    if (!this.body || !this.searchInput || !this.data.length) {
      return;
    }

    this.renderRows();
    this.rows = Array.from(this.querySelectorAll('[data-lookup-row]'));
    this.cardItems = Array.from(this.querySelectorAll('[data-lookup-card]'));
    this.searchInput.addEventListener('input', () => this.filterRows());
    this.rangeFilter.addEventListener('change', () => this.filterRows());
    this.sourceFilter.addEventListener('change', () => this.filterRows());
    this.resetButton.addEventListener('click', () => this.resetFilters());
    this.filterRows();
  }

  renderRows() {
    const rows = [];
    const cards = [];

    this.data.forEach((entry) => {
      const lookupText = [
        entry.itemNumber,
        entry.name,
        entry.characters,
        entry.film,
        entry.range,
        entry.series,
        entry.introduced,
        entry.source,
      ].join(' ').toLowerCase();

      const itemNumber = this.escape(entry.itemNumber);
      const name = this.escape(entry.name);
      const characters = entry.characters
        ? this.escape(entry.characters)
        : `<span class="disney-lookup__muted">${this.escape(this.labels.needsDetail)}</span>`;
      const film = entry.film
        ? this.escape(entry.film)
        : `<span class="disney-lookup__muted">${this.escape(this.labels.unknown)}</span>`;
      const range = this.escape(entry.range);
      const series = entry.series ? this.escape(entry.series) : '<span class="disney-lookup__muted">-</span>';
      const introduced = entry.introduced
        ? this.escape(entry.introduced)
        : `<span class="disney-lookup__muted">${this.escape(this.labels.unknown)}</span>`;
      const sourceUrl = this.buildMarketAwareUrl(entry.url);
      const source = sourceUrl
        ? `<a href="${this.escape(sourceUrl)}" target="_blank" rel="noopener">${this.escape(entry.source)}</a>`
        : this.escape(entry.source);
      const cardSummary = this.buildSummary(entry);
      const dataAttributes = `data-range="${range}" data-source="${this.escape(entry.source)}" data-lookup-text="${this.escape(lookupText)}"`;

      rows.push(`
        <tr data-lookup-row data-range="${this.escape(entry.range)}" data-source="${this.escape(entry.source)}" data-lookup-text="${this.escape(lookupText)}">
          <td class="disney-lookup__code">${itemNumber}</td>
          <td class="disney-lookup__name">${name}</td>
          <td>${characters}</td>
          <td>${film}</td>
          <td>${range}</td>
          <td>${series}</td>
          <td>${introduced}</td>
          <td class="disney-lookup__source">${source}</td>
        </tr>
      `);

      cards.push(`
        <article class="disney-lookup__card" data-lookup-card ${dataAttributes}>
          <div class="disney-lookup__card-header">
            <span class="disney-lookup__card-code">${itemNumber}</span>
            <span class="disney-lookup__card-range">${range}</span>
          </div>
          <h3 class="disney-lookup__card-title">${name}</h3>
          <p class="disney-lookup__card-summary">${this.escape(cardSummary)}</p>
          <details class="disney-lookup__card-details">
            <summary>${this.escape(this.labels.moreDetails)}</summary>
            <dl>
              <div>
                <dt>${this.escape(this.labels.characters)}</dt>
                <dd>${characters}</dd>
              </div>
              <div>
                <dt>${this.escape(this.labels.film)}</dt>
                <dd>${film}</dd>
              </div>
              <div>
                <dt>${this.escape(this.labels.series)}</dt>
                <dd>${series}</dd>
              </div>
              <div>
                <dt>${this.escape(this.labels.introduced)}</dt>
                <dd>${introduced}</dd>
              </div>
              <div>
                <dt>${this.escape(this.labels.source)}</dt>
                <dd>${source}</dd>
              </div>
            </dl>
          </details>
        </article>
      `);
    });

    this.body.innerHTML = rows.join('');
    if (this.cards) {
      this.cards.innerHTML = cards.join('');
    }
  }

  filterRows() {
    const query = this.searchInput.value.trim().toLowerCase();
    const range = this.rangeFilter.value;
    const source = this.sourceFilter.value;
    let visibleCount = 0;

    this.rows.forEach((row, index) => {
      const matchesQuery = !query || row.dataset.lookupText.includes(query);
      const matchesRange = !range || row.dataset.range === range;
      const matchesSource = !source || row.dataset.source === source;
      const isVisible = matchesQuery && matchesRange && matchesSource;

      row.hidden = !isVisible;
      if (this.cardItems[index]) {
        this.cardItems[index].hidden = !isVisible;
      }
      if (isVisible) {
        visibleCount += 1;
      }
    });

    this.resultCount.textContent = `${visibleCount} ${visibleCount === 1 ? this.labels.entry : this.labels.entries}`;
    this.emptyState.hidden = visibleCount > 0;
  }

  resetFilters() {
    this.searchInput.value = '';
    this.rangeFilter.value = '';
    this.sourceFilter.value = '';
    this.searchInput.focus();
    this.filterRows();
  }

  buildSummary(entry) {
    const details = [];

    if (entry.characters) {
      details.push(entry.characters);
    }

    if (entry.film) {
      details.push(entry.film);
    }

    if (details.length) {
      return details.join(' | ');
    }

    return this.labels.characterFilmVerified;
  }

  buildMarketAwareUrl(url) {
    if (!url) {
      return '';
    }

    try {
      const parsedUrl = new URL(url, window.location.origin);
      const internalHosts = ['collectable-figurines.co.uk', window.location.hostname];

      if (!internalHosts.includes(parsedUrl.hostname)) {
        return url;
      }

      const root = window.routes?.root_url || '/';
      const normalisedRoot = root === '/' ? '' : root.replace(/\/$/, '');

      return `${normalisedRoot}${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
    } catch (error) {
      return url;
    }
  }

  escape(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }
}

if (!customElements.get('disney-figurine-lookup')) {
  customElements.define('disney-figurine-lookup', DisneyFigurineLookup);
}
